import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export const usePresenceTransition = (isOpen, durationMs = 180) => {
  const [isPresent, setIsPresent] = useState(Boolean(isOpen));
  const [phase, setPhase] = useState(isOpen ? 'entered' : 'exited');

  useEffect(() => {
    let raf1 = 0;
    let raf2 = 0;
    let timeoutId = 0;

    if (isOpen) {
      setIsPresent(true);
      setPhase('entering');
      raf1 = window.requestAnimationFrame(() => {
        raf2 = window.requestAnimationFrame(() => {
          setPhase('entered');
        });
      });
    } else {
      if (!isPresent) {
        setPhase('exited');
      } else {
        setPhase('exiting');
        timeoutId = window.setTimeout(() => {
          setIsPresent(false);
          setPhase('exited');
        }, durationMs);
      }
    }

    return () => {
      if (raf1) window.cancelAnimationFrame(raf1);
      if (raf2) window.cancelAnimationFrame(raf2);
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [isOpen, durationMs, isPresent]);

  return { isPresent, phase };
};

export const useHeaderCompact = (
  headerRef,
  { throttleMs = 120, hysteresis = 120 } = {}
) => {
  const [threshold, setThreshold] = useState(0);
  const [isCompact, setIsCompact] = useState(false);

  useLayoutEffect(() => {
    const measure = () => {
      if (!headerRef?.current) return;
      const rect = headerRef.current.getBoundingClientRect();
      setThreshold(rect.height || 0);
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [headerRef]);

  useEffect(() => {
    if (!threshold) return;

    let rafId = 0;
    let throttleTimeoutId = 0;
    let lastRunTs = 0;

    const run = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        const y = window.scrollY;
        setIsCompact((prev) => {
          if (!prev && y >= threshold) return true;
          if (prev && y < threshold - hysteresis) return false;
          return prev;
        });
      });
    };

    const onScroll = () => {
      const now = Date.now();
      const elapsed = now - lastRunTs;
      if (elapsed >= throttleMs) {
        lastRunTs = now;
        run();
        return;
      }

      if (throttleTimeoutId) return;
      throttleTimeoutId = window.setTimeout(() => {
        throttleTimeoutId = 0;
        lastRunTs = Date.now();
        run();
      }, throttleMs - elapsed);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafId) window.cancelAnimationFrame(rafId);
      if (throttleTimeoutId) window.clearTimeout(throttleTimeoutId);
    };
  }, [threshold, throttleMs, hysteresis]);

  return isCompact;
};

export const useDismissOnOutsidePointer = (
  isOpen,
  { refs = [], onDismiss } = {}
) => {
  const onDismissRef = useRef(onDismiss);
  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    if (!isOpen) return;

    const onPointerDown = (e) => {
      const target = e.target;
      if (!target) return;
      if(document.querySelector('.ant-tabs-dropdown')?.contains(target)) return;

      for (const ref of refs) {
        const el = ref?.current;
        if (el && el.contains(target)) return;
      }

      if (onDismissRef.current) onDismissRef.current();
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [isOpen, refs]);
};

export const usePopoverPosition = (
  isOpen,
  {
    headerRef,
    searchRef,
    compute,
    throttleMs = 80,
    deps = [],
    initialRafCount = 0,
  }
) => {
  const [pos, setPos] = useState(() => ({ left: 0, top: 0 }));

  const computeRef = useRef(compute);
  useEffect(() => {
    computeRef.current = compute;
  }, [compute]);

  useLayoutEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      if (!headerRef?.current || !searchRef?.current) return;

      const headerRect = headerRef.current.getBoundingClientRect();
      const searchRect = searchRef.current.getBoundingClientRect();
      const next = computeRef.current({ headerRect, searchRect });
      if (next) setPos(next);
    };

    updatePosition();

    let rafIds = [];
    for (let i = 0; i < initialRafCount; i += 1) {
      const rafId = window.requestAnimationFrame(updatePosition);
      rafIds.push(rafId);
    }

    let rafId = 0;
    let throttleTimeoutId = 0;
    let lastRunTs = 0;

    const schedule = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        updatePosition();
      });
    };

    const throttledSchedule = () => {
      const now = Date.now();
      const elapsed = now - lastRunTs;
      if (elapsed >= throttleMs) {
        lastRunTs = now;
        schedule();
        return;
      }

      if (throttleTimeoutId) return;
      throttleTimeoutId = window.setTimeout(() => {
        throttleTimeoutId = 0;
        lastRunTs = Date.now();
        schedule();
      }, throttleMs - elapsed);
    };

    window.addEventListener('resize', throttledSchedule);
    window.addEventListener('scroll', throttledSchedule, { passive: true });
    return () => {
      window.removeEventListener('resize', throttledSchedule);
      window.removeEventListener('scroll', throttledSchedule);
      if (rafId) window.cancelAnimationFrame(rafId);
      for (const id of rafIds) window.cancelAnimationFrame(id);
      if (throttleTimeoutId) window.clearTimeout(throttleTimeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, headerRef, searchRef, throttleMs, ...deps]);

  return pos;
};

export const useExclusivePopoverToggles = ({
  setIsRegionOpen,
  setIsDatesOpen,
  setIsGuestsOpen,
  onRegionClick,
  onDatesClick,
  onGuestsClick,
}) => {
  const handleRegionToggle = useCallback(() => {
    setIsRegionOpen((prev) => !prev);
    setIsDatesOpen(false);
    if (onRegionClick) onRegionClick();
  }, [onRegionClick, setIsDatesOpen, setIsRegionOpen]);

  const handleDatesToggle = useCallback(() => {
    setIsDatesOpen((prev) => !prev);
    setIsRegionOpen(false);
    setIsGuestsOpen(false);
    if (onDatesClick) onDatesClick();
  }, [onDatesClick, setIsDatesOpen, setIsGuestsOpen, setIsRegionOpen]);

  const handleGuestsToggle = useCallback(() => {
    setIsGuestsOpen((prev) => !prev);
    setIsRegionOpen(false);
    setIsDatesOpen(false);
    if (onGuestsClick) onGuestsClick();
  }, [onGuestsClick, setIsDatesOpen, setIsGuestsOpen, setIsRegionOpen]);

  return { handleRegionToggle, handleDatesToggle, handleGuestsToggle };
};
